// Which providers this HOST can connect at all — the door, where the refresh trace beside it covers the engine.
//
// Why it is its own panel rather than a column on that one: a provider that cannot be connected can never
// reach the sync chain, so its rows there are correctly empty and say nothing about the cause. The two failures
// look identical from a product screen and have completely different fixes — one is "give this lane a
// credential", the other is "this band has not connected yet" — and until this page existed the only way to
// tell them apart was to press Connect and watch a popup open and shut.
//
// It is CONFIG ONLY: no vendor call, no database read, no writes. So there is no Run button, nothing to wait
// for, and no way for the diagnostic itself to be the thing that fails.
import { useState } from "react";
import { Card, Tag } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  useConnectDebug,
  type ConnectConfigKey,
  type ConnectProviderRow,
} from "../../app/hooks/useConnectDebug.js";
import styles from "./SyncChainPanel.module.scss";
import own from "./ConnectPanel.module.scss";

/** The credentials for one provider, each shown with what the boot dump would print for it. */
function Keys({ keys }: { keys: ConnectConfigKey[] }) {
  return (
    <ol className={styles.calls}>
      {keys.map((k) => (
        <li key={k.key} className={styles.call} data-ok={k.set === true}>
          <span className={styles.method}>{k.set ? "SET" : "UNSET"}</span>
          <span className={styles.url} title={k.key ?? ""}>
            {k.key}
          </span>
          {/* Redacted SERVER-side: a secret reads ***(set) / (null) and never leaves the host as a value.
              A non-secret reads verbatim, which is the point — a redirect URI that does not match the vendor
              console is the classic OAuth failure and is invisible from every other surface. */}
          <span className={styles.status}>{k.value}</span>
        </li>
      ))}
    </ol>
  );
}

function ProviderRow({ row }: { row: ConnectProviderRow }) {
  // Collapsed by default, exactly like the refresh trace's rows — the summary already names every missing key,
  // so the page is scannable without an interaction and the per-key detail (which redirect URI is actually
  // configured) is one click away.
  const [open, setOpen] = useState(false);
  const keys = row.keys ?? [];
  const missing = keys.filter((k) => k.set !== true);
  const configured = row.configured === true;

  return (
    <li className={styles.row}>
      <button
        type="button"
        className={styles.rowHead}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        disabled={keys.length === 0}
      >
        <span
          className={styles.light}
          data-status={configured ? "ok" : "fail"}
          aria-hidden="true"
        />
        <span className={styles.provider}>
          {row.provider}
          {row.requiresPkce ? <span className={styles.kind}>/pkce</span> : null}
        </span>
        {/* The line to read first. Naming the missing keys here rather than only inside the expanded list
            means the answer is on screen without an interaction — this page exists to be scanned. */}
        <span className={styles.summary}>
          {configured
            ? "credentials present — Connect can run"
            : `cannot connect: ${missing.map((k) => k.key).join(", ")} unset`}
        </span>
        <Tag tone={configured ? "success" : "danger"} size="sm">
          {configured ? "configured" : "setup pending"}
        </Tag>
      </button>

      {open && keys.length > 0 ? <Keys keys={keys} /> : null}
    </li>
  );
}

export function ConnectPanel() {
  const connect = useConnectDebug();

  if (connect.isPending) return <Card>Loading the connect estate…</Card>;
  if (connect.error) {
    return <Card>Could not load the connect estate: {errorMessage(connect.error, "the request failed")}</Card>;
  }

  const doc = connect.data;
  if (!doc) return null;

  const rows = doc.rows ?? [];
  const pending = rows.filter((r) => r.configured !== true);

  return (
    <Card>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Connect estate</h2>
          <p className={styles.subtitle}>
            Which providers this environment holds credentials for, and which key is missing when it does not.
          </p>
        </div>
      </div>

      <p className={own.summary} data-pending={pending.length > 0}>
        env <code>{doc.envName}</code>
        {pending.length === 0
          ? " — every registered connect provider is configured here"
          : ` — ${pending.length} of ${rows.length} cannot be connected on this host: ${pending
              .map((r) => r.provider)
              .join(", ")}`}
      </p>

      <ul className={styles.rows}>
        {rows.map((row) => (
          <ProviderRow key={row.provider} row={row} />
        ))}
      </ul>

      {/* Named so their ABSENCE from the list above cannot be read as an omission — neither has an OAuth
          strategy and neither should. The same reason the refresh trace names what it does not sync. */}
      {(doc.notOAuth ?? []).length > 0 ? (
        <p className={styles.notSynced}>
          Not authorization-code OAuth, so not listed above: {(doc.notOAuth ?? []).join(" · ")}
        </p>
      ) : null}
    </Card>
  );
}
